import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { loadSession, saveSession, clearSession } from "../lib/auth-store";
import {
  loginAccount,
  registerAccount,
  fetchMe,
  logoutAccount,
  type Account,
} from "../lib/identity";
import { verify2fa, emailMfaVerify } from "../lib/twofa";

export interface LoginResult {
  mfaRequired: boolean;
  factorId?: string | null;
  emailMfaRequired?: boolean;
}

interface AuthState {
  account: Account | null;
  roles: string[];
  loading: boolean;
  login: (email: string, password: string, turnstileToken?: string | null) => Promise<LoginResult>;
  completeMfa: (factorId: string, code: string) => Promise<void>;
  completeEmailMfa: (code: string) => Promise<void>;
  register: (email: string, password: string, turnstileToken?: string | null) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [account, setAccount] = useState<Account | null>(null);
  const [roles, setRoles] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  async function refresh(): Promise<void> {
    const session = loadSession();
    if (!session) {
      setAccount(null);
      setRoles([]);
      setLoading(false);
      return;
    }
    try {
      const me = await fetchMe(session.access_token);
      setAccount(me.account);
      setRoles(me.roles);
    } catch {
      clearSession();
      setAccount(null);
      setRoles([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  async function login(email: string, password: string, turnstileToken?: string | null): Promise<LoginResult> {
    const session = await loginAccount(email, password, turnstileToken);
    // Save the (aal1) session so the 2fa-verify call can authenticate.
    saveSession({
      access_token: session.access_token,
      refresh_token: session.refresh_token,
      user_id: session.user_id,
    });
    if (session.mfa_required) {
      // Hold off on loading the account until the second factor is verified.
      return { mfaRequired: true, factorId: session.factor_id };
    }
    if (session.email_mfa_required) {
      // A code was emailed; hold off until it's verified (email-2fa-verify marks
      // this session — the same token then passes the server guard).
      return { mfaRequired: false, emailMfaRequired: true };
    }
    setLoading(true);
    await refresh();
    return { mfaRequired: false };
  }

  async function completeMfa(factorId: string, code: string): Promise<void> {
    const res = await verify2fa(factorId, code);
    if (res.access_token && res.refresh_token) {
      const prev = loadSession();
      saveSession({
        access_token: res.access_token,
        refresh_token: res.refresh_token,
        user_id: prev?.user_id ?? "",
      });
    }
    setLoading(true);
    await refresh();
  }

  async function completeEmailMfa(code: string): Promise<void> {
    // Marks the current session verified server-side; the existing token then
    // passes the guard — no token swap needed.
    await emailMfaVerify(code);
    setLoading(true);
    await refresh();
  }

  async function register(email: string, password: string, turnstileToken?: string | null): Promise<void> {
    // Register signs the account in and returns a session, so no second call
    // (and no second captcha) is needed.
    const session = await registerAccount(email, password, turnstileToken);
    saveSession({
      access_token: session.access_token,
      refresh_token: session.refresh_token,
      user_id: session.user_id,
    });
    setLoading(true);
    await refresh();
  }

  async function logout(): Promise<void> {
    const session = loadSession();
    if (session) {
      try {
        await logoutAccount(session.access_token);
      } catch {
        /* best-effort */
      }
    }
    clearSession();
    setAccount(null);
    setRoles([]);
  }

  return (
    <AuthContext.Provider value={{ account, roles, loading, login, completeMfa, completeEmailMfa, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
}
